/**
 * ProductHeroIdentifiers — kopierbare Kennungen (SKU/EAN/ASIN/Externe ID).
 * Max 100 lines.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Product } from '@/types/products';
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';

function CopyableId({ label, value, eidKey }: { label: string; value: string; eidKey: string }) {
  const { t } = useTranslation();
  const copy = useCallback(async () => {
    await navigator.clipboard.writeText(value);
    toast.success(t('common.copied'));
  }, [value, t]);

  return (
    <TooltipProvider>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <button
            id={eidKey}
            onClick={copy}
            className="font-mono text-xs text-neutral-600 dark:text-neutral-400 hover:text-green-700 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-950 px-1 py-0.5 rounded transition-colors"
          >
            <span className="text-neutral-400 dark:text-neutral-500 uppercase tracking-wide mr-1">{label}</span>
            {value}
          </button>
        </TooltipTrigger>
        <TooltipContent>{label} {t('products.copyLabelSuffix')}</TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export function ProductHeroIdentifiers({ product }: { product: Product }) {
  const { t } = useTranslation();

  return (
    <div className="product-hero-identifiers flex items-center gap-1.5 flex-wrap">
      {product.sku && (
        <CopyableId label="SKU" value={product.sku} eidKey={EID['product-hero-copy-sku-btn']} />
      )}
      {product.gtin && (
        <CopyableId label="EAN" value={product.gtin} eidKey={EID['product-hero-copy-ean-btn']} />
      )}
      {product.asin && (
        <CopyableId label="ASIN" value={product.asin} eidKey={EID['product-hero-copy-asin-btn']} />
      )}
      {product.external_id && (
        <CopyableId
          label={t('products.externalId')}
          value={product.external_id}
          eidKey={EID['product-hero-copy-external-id-btn']}
        />
      )}
    </div>
  );
}
